// src/services/commonPartsService.js
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { PART_TYPES } from '../types/commonParts';

/**
 * 共通パーツ一覧を取得
 */
export const getCommonParts = async (accountId, type = null) => {
    try {
        const partsRef = collection(db, 'accounts', accountId, 'commonParts');

        // まずはすべてのパーツを取得（orderByなし）
        let q = query(partsRef);

        if (type) {
            q = query(partsRef, where('type', '==', type));
        }

        const snapshot = await getDocs(q);

        // クライアント側でソート
        let parts = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        // 作成日時でソート
        parts.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA; // 降順
        });

        return parts;
    } catch (error) {
        console.error('共通パーツ一覧の取得に失敗:', error);
        throw error;
    }
};

/**
 * 特定の共通パーツを取得
 */
export const getCommonPartById = async (accountId, partId) => {
    try {
        const partRef = doc(db, 'accounts', accountId, 'commonParts', partId);
        const partDoc = await getDoc(partRef);

        if (!partDoc.exists()) {
            throw new Error('共通パーツが見つかりません');
        }

        return {
            id: partDoc.id,
            ...partDoc.data()
        };
    } catch (error) {
        console.error('共通パーツの取得に失敗:', error);
        throw error;
    }
};

/**
 * 共通パーツを作成
 */
export const createCommonPart = async (accountId, partData) => {
    try {

        const partsRef = collection(db, 'accounts', accountId, 'commonParts');

        // データをフラットに展開して、ネストされたオブジェクトを避ける
        const newPart = {
            name: typeof partData.name === 'string' ? partData.name : String(partData.name || ''),
            description: typeof partData.description === 'string' ? partData.description : String(partData.description || ''),
            type: typeof partData.type === 'string' ? partData.type : String(partData.type || ''),
            html: typeof partData.html === 'string' ? partData.html : String(partData.html || ''),
            css: typeof partData.css === 'string' ? partData.css : String(partData.css || ''),
            createdAt: new Date(),
            updatedAt: new Date()
        };


        // 新しいドキュメントIDを生成
        const newDocRef = doc(partsRef);
        await setDoc(newDocRef, newPart);

        return {
            id: newDocRef.id,
            ...newPart
        };
    } catch (error) {
        console.error('共通パーツの作成に失敗:', error);
        throw error;
    }
};

/**
 * 共通パーツを更新
 */
export const updateCommonPart = async (accountId, partId, updateData) => {
    try {

        // updateDataがnullやundefinedの場合はエラー
        if (!updateData || typeof updateData !== 'object') {
            throw new Error('更新データが無効です');
        }

        const partRef = doc(db, 'accounts', accountId, 'commonParts', partId);

        // データをフラットに展開して、ネストされたオブジェクトを避ける
        const updatePayload = {};

        // 各フィールドを個別に設定（undefinedやnullを避ける）
        if (updateData.name !== undefined) {
            updatePayload.name = typeof updateData.name === 'string' ? updateData.name : String(updateData.name || '');
        }
        if (updateData.description !== undefined) {
            updatePayload.description = typeof updateData.description === 'string' ? updateData.description : String(updateData.description || '');
        }
        if (updateData.type !== undefined) {
            updatePayload.type = typeof updateData.type === 'string' ? updateData.type : String(updateData.type || '');
        }
        if (updateData.html !== undefined) {
            updatePayload.html = typeof updateData.html === 'string' ? updateData.html : String(updateData.html || '');
        }
        if (updateData.css !== undefined) {
            updatePayload.css = typeof updateData.css === 'string' ? updateData.css : String(updateData.css || '');
        }

        updatePayload.updatedAt = new Date();


        // バッチを使用して安全に更新
        const batch = writeBatch(db);

        // 既存のドキュメントを取得
        const existingDoc = await getDoc(partRef);
        if (!existingDoc.exists()) {
            throw new Error('共通パーツが見つかりません');
        }

        const existingData = existingDoc.data();
        const mergedData = {
            ...existingData,
            ...updatePayload
        };

        // バッチに更新を追加
        batch.set(partRef, mergedData);

        // バッチをコミット
        await batch.commit();

        return {
            id: partId,
            ...updatePayload
        };
    } catch (error) {
        console.error('共通パーツの更新に失敗:', error);
        throw error;
    }
};

/**
 * 共通パーツを削除
 */
export const deleteCommonPart = async (accountId, partId) => {
    try {
        const partRef = doc(db, 'accounts', accountId, 'commonParts', partId);
        await deleteDoc(partRef);
    } catch (error) {
        console.error('共通パーツの削除に失敗:', error);
        throw error;
    }
};


/**
 * ヘッダー用の共通パーツを取得
 */
export const getHeaderParts = async (accountId) => {
    return await getCommonParts(accountId, PART_TYPES.HEADER);
};

/**
 * フッター用の共通パーツを取得
 */
export const getFooterParts = async (accountId) => {
    return await getCommonParts(accountId, PART_TYPES.FOOTER);
};

/**
 * 共通パーツの使用状況をチェック
 */
export const checkPartUsage = async (accountId, partId) => {
    try {
        // ページで使用されているかチェック
        const pagesRef = collection(db, 'pages');
        const pagesQuery = query(
            pagesRef,
            where('accountId', '==', accountId),
            where('headerPartId', '==', partId)
        );
        const pagesSnapshot = await getDocs(pagesQuery);

        const footerQuery = query(
            pagesRef,
            where('accountId', '==', accountId),
            where('footerPartId', '==', partId)
        );
        const footerSnapshot = await getDocs(footerQuery);

        return {
            isUsed: pagesSnapshot.size > 0 || footerSnapshot.size > 0,
            usedInPages: pagesSnapshot.size,
            usedInFooters: footerSnapshot.size
        };
    } catch (error) {
        console.error('パーツ使用状況の確認に失敗:', error);
        return { isUsed: false, usedInPages: 0, usedInFooters: 0 };
    }
};
